import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'

// Resolve the real breakpoint synchronously from the viewport instead of
// hardcoding a default that gets corrected later. See the note above
// `currentBreakpoint`/`isVerticalMenuActive` below for why this matters.
const getBreakpoint = () => {
  if (typeof window === 'undefined') return 'xl'
  if (window.innerWidth >= 1200) return 'xl'
  if (window.innerWidth >= 992) return 'lg'
  if (window.innerWidth >= 768) return 'md'
  if (window.innerWidth >= 576) return 'sm'
  return 'xs'
}

export default function useVerticalLayout(navbarType, footerType) {
  // IMPORTANT: both refs used to default to hardcoded values (`isVerticalMenuActive
  // = true`, `currentBreakpoint = 'xl'`) and only got corrected by `resizeHandler()`
  // + the `watch()` below, once `setup()` ran. But a Vue `watch()` callback —
  // even for a change made synchronously during setup() — only runs on Vue's
  // next tick, which happens AFTER the first render already painted. On
  // mobile that meant the very first render still saw the stale
  // `isVerticalMenuActive = true`, so `overlayClasses` (and the `menu-open`
  // vs `menu-hide` layout class) briefly evaluated as "menu open" and
  // rendered the full-screen dark `.sidenav-overlay` backdrop over the page.
  // On a lightweight page that window is imperceptible; on a heavy one (the
  // dashboard mounts several ApexCharts components) it was long enough to
  // look like a blank/stuck screen until some interaction (scroll, tap)
  // finally let the pending correction flush. Fixed by computing both refs'
  // correct values up front, synchronously, so the first render is already
  // right — no dependency on watcher timing at all.
  const currentBreakpoint = ref(getBreakpoint())
  const isVerticalMenuActive = ref(currentBreakpoint.value === 'xl')

  const toggleVerticalMenuActive = () => {
    isVerticalMenuActive.value = !isVerticalMenuActive.value
  }

  const isVerticalMenuCollapsed = computed(() => store.state.verticalMenu.isVerticalMenuCollapsed)

  const layoutClasses = computed(() => {
    const classes = []

    if (currentBreakpoint.value === 'xl') {
      classes.push('vertical-menu-modern')
      classes.push(isVerticalMenuCollapsed.value ? 'menu-collapsed' : 'menu-expanded')
    } else {
      classes.push('vertical-overlay-menu')
      classes.push(isVerticalMenuActive.value ? 'menu-open' : 'menu-hide')
    }

    // Navbar
    classes.push(`navbar-${navbarType.value}`)

    // Footer
    if (footerType.value === 'sticky') classes.push('footer-fixed')
    if (footerType.value === 'static') classes.push('footer-static')
    if (footerType.value === 'hidden') classes.push('footer-hidden')

    return classes
  })

  // ------------------------------------------------
  // Resize handler for Breakpoint
  // ------------------------------------------------
  watch(currentBreakpoint, val => {
    isVerticalMenuActive.value = val === 'xl'
  })

  const resizeHandler = () => {
    // ? This closes vertical menu when title bar is shown/hidden in mobile browsers.
    // ? We will watch for breakpoint to overcome this issue
    // isVerticalMenuActive.value = window.innerWidth >= 1200
    currentBreakpoint.value = getBreakpoint()
  }

  const overlayClasses = computed(() => {
    if (currentBreakpoint.value !== 'xl' && isVerticalMenuActive.value) return 'show'
    return null
  })

  const isMobileViewport = computed(() => currentBreakpoint.value !== 'xl')

  const navbarTypeClass = computed(() => {
    if (navbarType.value === 'sticky') return 'fixed-top'
    if (navbarType.value === 'static') return 'navbar-static-top'
    if (navbarType.value === 'hidden') return 'd-none'
    return 'floating-nav'
  })

  const footerTypeClass = computed(() => {
    if (footerType.value === 'static') return 'footer-static'
    if (footerType.value === 'hidden') return 'd-none'
    return ''
  })

  return {
    isVerticalMenuActive,
    toggleVerticalMenuActive,
    isVerticalMenuCollapsed,
    layoutClasses,
    overlayClasses,
    isMobileViewport,
    navbarTypeClass,
    footerTypeClass,
    resizeHandler,
  }
}
